import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, CloudOff, Download, CheckCircle2, AlertCircle, RefreshCw, ArrowRight, Shield, Database } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
type BackupState = 'checking' | 'found' | 'notFound' | 'restoring' | 'success' | 'error';
interface BackupCheckScreenProps {
  onContinue: () => void;
  onBack: () => void;
}
export function BackupCheckScreen({
  onContinue,
  onBack
}: BackupCheckScreenProps) {
  const {
    isLight
  } = useTheme();
  const [state, setState] = useState<BackupState>('checking');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanText, setScanText] = useState('Connecting to cloud...');
  // Simulate backup check — randomly finds or doesn't find backup
  useEffect(() => {
    if (state !== 'checking') return;
    const steps = [{
      progress: 20,
      text: 'Connecting to cloud...',
      delay: 800
    }, {
      progress: 45,
      text: 'Searching Google Drive...',
      delay: 1200
    }, {
      progress: 70,
      text: 'Scanning iCloud...',
      delay: 1000
    }, {
      progress: 90,
      text: 'Analyzing backup data...',
      delay: 800
    }, {
      progress: 100,
      text: 'Complete',
      delay: 600
    }];
    let timeout: ReturnType<typeof setTimeout>;
    let elapsed = 0;
    steps.forEach((step, i) => {
      elapsed += step.delay;
      timeout = setTimeout(() => {
        setScanProgress(step.progress);
        setScanText(step.text);
        if (i === steps.length - 1) {
          // 60% chance of finding backup for demo
          setTimeout(() => {
            setState(Math.random() > 0.4 ? 'found' : 'notFound');
          }, 500);
        }
      }, elapsed);
    });
    return () => clearTimeout(timeout);
  }, [state]);
  const handleRestore = () => {
    setState('restoring');
    // Simulate restore process
    setTimeout(() => {
      // 80% success rate for demo
      setState(Math.random() > 0.2 ? 'success' : 'error');
    }, 3000);
  };
  const handleRetry = () => {
    setScanProgress(0);
    setScanText('Connecting to cloud...');
    setState('checking');
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-765" data-mp="1uhpgwlfku4ze">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/15' : 'bg-blue-500/10'}`} data-id="element-766" data-mp="2r5vh8w5gub7v" />
      <div className={`absolute bottom-32 -left-16 w-40 h-40 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/5'}`} data-id="element-767" data-mp="2i4xjuwpj91y9" />

      <div className="flex-1 flex flex-col items-center justify-center relative z-10" data-id="element-768" data-mp="16qk0a41tb8o1">
        <AnimatePresence mode="wait" data-id="element-769" data-mp="16oh2uyybyd9a">
          {/* ─── CHECKING STATE ─── */}
          {state === 'checking' && <motion.div key="checking" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-770" data-mp="rnjvd1efzznn">
              <div className="relative flex items-center justify-center mb-8" data-id="element-771" data-mp="1aetbhtipxmo7">
                {/* Pulsing outer circle */}
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`absolute w-24 h-24 rounded-full ${isLight ? 'bg-blue-500/20' : 'bg-blue-400/20'}`} data-id="element-772" data-mp="1fbjou935245x" />
                {/* Rotating inner ring */}
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className={`absolute w-16 h-16 rounded-full border-2 border-transparent ${isLight ? 'border-t-blue-500' : 'border-t-blue-400'}`} data-id="element-773" data-mp="1lctpjxetd3ks" />
                {/* Breathing center icon */}
                <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-774" data-mp="xq3ooze7bf1r">
                  <Cloud className={`w-8 h-8 ${isLight ? 'text-blue-500' : 'text-blue-400'}`} data-id="element-775" data-mp="iylc7bkjw2n5" />
                </motion.div>
              </div>

              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-776" data-mp="2pnwvx9dyab19">
                Checking for Backup
              </p>
              <motion.p key={scanText} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-777" data-mp="2go9puaaa0h1v">
                {scanText}
              </motion.p>
            </motion.div>}

          {/* ─── BACKUP FOUND ─── */}
          {state === 'found' && <motion.div key="found" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-778" data-mp="34d2q2ixxlb2a">
              <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-vexu/10 border-vexu/20' : 'bg-vexu/10 border-vexu/20'}`} data-id="element-779" data-mp="14ug65jbssgoq">
                <Database className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-780" data-mp="h43npeixf1jw" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-781" data-mp="c7lnmryjjrdw">
                Backup found!
              </h1>
              <p className={`text-sm mb-8 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-782" data-mp="26k32bb5bdj6x">
                We found your Vexu data in the cloud.
              </p>

              {/* Backup details card */}
              <div className={`w-full backdrop-blur-xl border rounded-2xl p-5 mb-8 ${isLight ? 'bg-white/60 border-slate-200/50 shadow-sm' : 'bg-dark-card/60 border-white/10'}`} data-id="element-783" data-mp="2nsvu68xmf9qb">
                <div className="space-y-3" data-id="element-784" data-mp="1v4gls58zvpny">
                  <div className="flex justify-between items-center" data-id="element-785" data-mp="1v5roe1qperdh">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-786" data-mp="202i42nyljkyd">
                      Provider
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-787" data-mp="294824cmzhi54">
                      Google Drive
                    </span>
                  </div>
                  <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-788" data-mp="3khk2ueng5kn2" />
                  <div className="flex justify-between items-center" data-id="element-789" data-mp="5g7tk9hj63yl">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-790" data-mp="5i0vxcl4220s">
                      Last backup
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-791" data-mp="fcart499hig9">
                      Mar 12, 2026
                    </span>
                  </div>
                  <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-792" data-mp="133oil5rrl5z4" />
                  <div className="flex justify-between items-center" data-id="element-793" data-mp="y66lqdr0uh7h">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-794" data-mp="34utbe55focl1">
                      Size
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-795" data-mp="1fefrrjqk4z7k">
                      24.3 MB
                    </span>
                  </div>
                  <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-796" data-mp="15ncxb0s4vih9" />
                  <div className="flex justify-between items-center" data-id="element-797" data-mp="1aleax66siplc">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-798" data-mp="wmwvdvr1gjei">
                      Includes
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-799" data-mp="mqtqii4sy6nv">
                      142 calls, 4 groups
                    </span>
                  </div>
                </div>
              </div>

              <div className="w-full space-y-3" data-id="element-800" data-mp="2m2w9zsdff11j">
                <Button fullWidth size="lg" onClick={handleRestore} data-id="element-801" data-mp="2m0jqhas0simd">
                  <Download className="w-4 h-4 mr-2" data-id="element-802" data-mp="2m7lyhxf5u55k" />
                  Restore Backup
                </Button>
                <button onClick={onContinue} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-803" data-mp="2r0ezqbbf6vcw">
                  Skip and start fresh
                </button>
              </div>
            </motion.div>}

          {/* ─── NOT FOUND ─── */}
          {state === 'notFound' && <motion.div key="notFound" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-804" data-mp="2vygrrlqtq50i">
              <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`} data-id="element-805" data-mp="1hxu77hex5rvi">
                <CloudOff className={`w-10 h-10 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-806" data-mp="uacbjasja9n9" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-807" data-mp="139zfa38qjrjh">
                No backup found
              </h1>
              <p className={`text-sm mb-3 max-w-[280px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-808" data-mp="fhscal8i8mb3">
                We couldn't find any existing Vexu data linked to this number.
                Let's set up your account fresh.
              </p>

              <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full mb-8 ${isLight ? 'bg-slate-50' : 'bg-white/5'}`} data-id="element-809" data-mp="2fkbdu0w4v41u">
                <Shield className={`w-3.5 h-3.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-810" data-mp="2pb6198l489ov" />
                <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-811" data-mp="1rqjrzqiya4bf">
                  You can enable backups later in Settings
                </span>
              </div>

              <div className="w-full" data-id="element-812" data-mp="25nym38k5f09q">
                <Button fullWidth size="lg" onClick={onContinue} data-id="element-813" data-mp="25ozybgcwhst1">
                  Continue Setup
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-814" data-mp="3d10dpp1ivw8l" />
                </Button>
              </div>
            </motion.div>}

          {/* ─── RESTORING ─── */}
          {state === 'restoring' && <motion.div key="restoring" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-815" data-mp="15fig4lvr0m22">
              <div className="relative flex items-center justify-center mb-8" data-id="element-816" data-mp="mn64f8j7g8f9">
                {/* Pulsing outer circle */}
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`absolute w-24 h-24 rounded-full ${isLight ? 'bg-vexu/20' : 'bg-vexu/20'}`} data-id="element-817" data-mp="doc87j9sdk28" />
                {/* Rotating inner ring */}
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className={`absolute w-16 h-16 rounded-full border-2 border-transparent ${isLight ? 'border-t-vexu-600' : 'border-t-vexu'}`} data-id="element-818" data-mp="14rppa2js0uh0" />
                {/* Breathing center icon */}
                <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-819" data-mp="luy41nkoc5ab">
                  <Download className={`w-8 h-8 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-820" data-mp="32m5zd11zq5z" />
                </motion.div>
              </div>

              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-821" data-mp="3foasve20ripb">
                Retrieving Backup
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-822" data-mp="3plp9bdny07hm">
                Please don't close the app...
              </p>
            </motion.div>}

          {/* ─── SUCCESS ─── */}
          {state === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-823" data-mp="3tlg3lrcoh5qw">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15,
            delay: 0.1
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-vexu/10 border-vexu/20' : 'bg-vexu/10 border-vexu/20'}`} data-id="element-824" data-mp="h2ho3jxh6apy">
                <CheckCircle2 className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-825" data-mp="1bwtxnk00z5nx" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-826" data-mp="1gudb4howfeet">
                Restore complete!
              </h1>
              <p className={`text-sm mb-3 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-827" data-mp="3ipdzte6ieuce">
                Your call history, groups, and settings have been restored
                successfully.
              </p>

              <div className={`flex flex-wrap justify-center gap-2 mb-8`} data-id="element-828" data-mp="200t7815kjvki">
                {['142 calls', '4 groups', 'AI settings'].map(item => <span key={item} className={`text-xs font-medium px-3 py-1.5 rounded-full ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-829" data-mp="1vxdehfwmefd3">
                    {item}
                  </span>)}
              </div>

              <div className="w-full" data-id="element-830" data-mp="2jn7q5u57dxvr">
                <Button fullWidth size="lg" onClick={onContinue} data-id="element-831" data-mp="2ep6553o7vohn">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-832" data-mp="29rnun8ujx17j" />
                </Button>
              </div>
            </motion.div>}

          {/* ─── ERROR ─── */}
          {state === 'error' && <motion.div key="error" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-833" data-mp="ybfypq9mko4u">
              <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-red-50 border-red-100' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-834" data-mp="2tfxay398iabo">
                <AlertCircle className={`w-10 h-10 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-835" data-mp="3wdfivo82po4" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-836" data-mp="1teikweijsh4j">
                Restore failed
              </h1>
              <p className={`text-sm mb-8 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-837" data-mp="234lpzd2y1lj8">
                Something went wrong while restoring your data. Please check
                your connection and try again.
              </p>

              <div className="w-full space-y-3" data-id="element-838" data-mp="1ai0ymcmgx9fo">
                <Button fullWidth size="lg" onClick={handleRetry} data-id="element-839" data-mp="15i4vsgpibo1p">
                  <RefreshCw className="w-4 h-4 mr-2" data-id="element-840" data-mp="1jgmeu14gwcda" />
                  Try Again
                </Button>
                <button onClick={onContinue} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-841" data-mp="1fereozy9w513">
                  Skip and start fresh
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}